import React, { useContext, useEffect } from 'react';

import { Box, Container, Typography } from '@mui/material';
import isEqual from 'react-fast-compare';
import { useTranslation } from 'react-i18next';
import { InstantSearch } from 'react-instantsearch-dom';
import { useHistory, useLocation } from 'react-router-dom';

import { Loader } from '../../../../components/Loader';
import { DiscoverContext } from '../../../../context/DiscoverContext';
import useScriptLoader, { SCRIPTS } from '../../../../context/ScriptLoader/useScriptLoader';
import { LearnerInfo, Tenant } from '../../../../types';
import { useClearSearchState, useSERPNavigation } from '../../../../utils/hooks';
import Breadcrumbs from '../../components/Breadcrumbs';
import { ControlledTabs } from '../../components/ControlledTabs';
import { Header } from '../../components/Header';
import { useCommonHeaderStyles } from '../../components/Header/style';
import SERPContainer from '../../components/SERPContainer';
import { SearchBox } from '../../components/SearchBox';
import { SearchSeoBlock } from '../../components/SearchSeoBlock';
import { Tabs } from '../../components/Tabs';
import { HITS_TYPES } from '../../const';
import {
  createURL,
  formatSearchState,
  getNavigationArray,
  searchStateToUrl,
  urlToSearchState,
} from './helpers';
import useStyles from './styles';

interface SearchPageProps {
  tenant: Tenant;
  algoliaIndexName: string;
  searchClient: any;
  userInfo?: LearnerInfo;
  loadingKey: boolean;
  logoUrl?: string;
  tenantName?: string | null;
}

const SearchPage: React.FC<SearchPageProps> = ({
  tenant,
  algoliaIndexName,
  searchClient,
  userInfo,
  loadingKey,
  logoUrl,
  tenantName,
}) => {
  useScriptLoader(SCRIPTS.TEAMS_SHARE);
  const { t } = useTranslation();
  const headerClasses = useCommonHeaderStyles();

  const { searchState, setSearchState } = useContext(DiscoverContext);
  const selectedTab = searchState?.refinementList?.searchable_type;

  const location = useLocation();
  const type = useSERPNavigation();
  const classes = useStyles(!searchState?.query)();

  const history = useHistory();

  const onSearchStateChange = (newState) => {
    setSearchState(newState);
    history.push(searchStateToUrl(location, newState, type), newState);
  };

  //clear search state on page change
  useClearSearchState(true);

  const navigationArr = getNavigationArray(selectedTab);

  const handleClearSearch = () => {
    onSearchStateChange({
      ...searchState,
      query: '',
    });
  };

  //set searchable_type from query string params
  useEffect(() => {
    if (type || type === '') {
      setSearchState((prevState) => ({
        ...prevState,
        refinementList: {
          ...prevState?.refinementList,
          searchable_type: type,
        },
      }));
    }
  }, [type, setSearchState]);

  //update state from query string params
  useEffect(() => {
    const nextSearchState = urlToSearchState(location, type);
    const formattedSearchState = formatSearchState(searchState, type);

    if (!isEqual(formattedSearchState, nextSearchState)) {
      setSearchState((prevState) => {
        return {
          ...prevState,
          ...nextSearchState,
          refinementList: {
            ...prevState?.refinementList,
            ...nextSearchState?.refinementList,
          },
        };
      });
    }

    // eslint-disable-next-line
  }, [location, type, setSearchState]);

  if (loadingKey) {
    return (
      <>
        <SearchSeoBlock tenantTitle={tenant?.title} />
        <Loader />
      </>
    );
  }

  return (
    <>
      <SearchSeoBlock tenantTitle={tenant?.title} />
      <InstantSearch
        searchState={searchState}
        onSearchStateChange={onSearchStateChange}
        searchClient={searchClient}
        indexName={algoliaIndexName}
        createURL={createURL}
      >
        <Header title={t('page.discover.discover')} tenantName={tenantName}>
          <Container className={headerClasses.centeredContainer}>
            <SearchBox
              wrapperClass={headerClasses.search}
              withClear={true}
              handleClear={handleClearSearch}
            />
            {type === '' ? (
              <Tabs selectedItem={''} />
            ) : (
              <ControlledTabs attribute="searchable_type" withLinks />
            )}
          </Container>
        </Header>
        <Container data-testid="searchPage">
          <Breadcrumbs navigation={navigationArr} />
          <Box className={classes.descriptionContainer}>
            {selectedTab === HITS_TYPES.VIDEO && (
              <Box className={headerClasses.wrapperWithBorder}>
                <Typography
                  data-testid="tabContentTitle"
                  variant="h2"
                  className={headerClasses.searchTitle}
                >
                  {t('page.discover.videos')}
                </Typography>
                <Typography
                  data-testid="tabContentDescription"
                  className={headerClasses.searchDescription}
                >
                  {t('page.discover.videosDescription')}
                </Typography>
              </Box>
            )}
            {selectedTab === HITS_TYPES.PATHWAY && (
              <Box className={headerClasses.wrapperWithBorder}>
                <Typography
                  data-testid="tabContentTitle"
                  variant="h2"
                  className={headerClasses.searchTitle}
                >
                  {t('page.discover.pathways')}
                </Typography>
                <Typography
                  data-testid="tabContentDescription"
                  className={headerClasses.searchDescription}
                >
                  {t('page.discover.pathwaysDescription')}
                </Typography>
              </Box>
            )}
            {selectedTab === HITS_TYPES.NEWS && (
              <Box className={headerClasses.wrapperWithBorder}>
                <Typography
                  data-testid="tabContentTitle"
                  variant="h2"
                  className={headerClasses.searchTitle}
                >
                  {t('page.discover.text')}
                </Typography>
                <Typography
                  data-testid="tabContentDescription"
                  className={headerClasses.searchDescription}
                >
                  {t('page.discover.textDescription')}
                </Typography>
              </Box>
            )}
            {selectedTab === HITS_TYPES.GLOSSARY && (
              <Box className={headerClasses.wrapperWithBorder}>
                <Typography
                  data-testid="tabContentTitle"
                  variant="h2"
                  className={headerClasses.searchTitle}
                >
                  {t('page.discover.glossary')}
                </Typography>
                <Typography
                  data-testid="tabContentDescription"
                  className={headerClasses.searchDescription}
                >
                  {t('page.discover.glossaryDescription')}
                </Typography>
              </Box>
            )}
          </Box>
          <SERPContainer
            logoUrl={logoUrl}
            userInfo={userInfo}
            tenantTitle={tenant?.title}
            tenantName={tenant?.name}
            linkedInId={tenant?.linkedin_id}
            updateLocationOnClear
          />
        </Container>
      </InstantSearch>
    </>
  );
};

export default SearchPage;
